import {
    channelProgramInfoQuery,
    ChannelProgramInfo,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createResourceQuery } from '@funtech-int/rival';
import { useResources } from '@funtech-int/rival-react';

import type { ChannelProgramModalErrorCallbackInfo } from '@/scenarios/legacy-player/components/channel-program/analytics/builders/channelProgramModalErrorParamsBuilder';
import { GraphqlClient } from '@/shared/lib/graphqlClient';
import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';

const CHANNEL_PROGRAM_INFO_CACHE_LIFETIME_MS = 2 * 60 * 60 * 1000;

interface ChannelProgramInfoQueryContext {
    client: GraphqlClient;
    contentId: string;
    onRequestFailed: (info: ChannelProgramModalErrorCallbackInfo) => void;
}

interface ChannelProgramInfoResourceResult {
    data: ChannelProgramInfo | null | undefined;
}

const channelProgramInfoResourceQuery = createResourceQuery<
    ChannelProgramInfoQueryContext,
    ChannelProgramInfoResourceResult
>({
    key(ctx) {
        return [channelProgramInfoQuery.name, ctx.contentId].join(':');
    },

    fetch(ctx) {
        return ctx.client
            .fetch({
                operation: channelProgramInfoQuery,
                variables: { contentId: ctx.contentId },
            })
            .then((response) => {
                if (response.errors?.length) {
                    const error = response.errors[0];

                    if (error) {
                        ctx.onRequestFailed({
                            message: error.message,
                            name: error.name,
                        });
                    }
                }

                return response as ChannelProgramInfoResourceResult;
            })
            .catch((error: unknown) => {
                ctx.onRequestFailed({
                    message: error instanceof Error ? error.message : String(error),
                    name: error instanceof Error ? error.name : undefined,
                });

                return {
                    data: null,
                };
            });
    },

    toJSON(response: ChannelProgramInfoResourceResult) {
        return {
            data: response.data,
        };
    },

    hydrate(serialized: object): ChannelProgramInfoResourceResult {
        const { data } = serialized as ChannelProgramInfoResourceResult;

        return {
            data: data == null ? data : channelProgramInfoQuery.hydrate(data as never),
        };
    },
});

interface UseChannelProgramInfoQueryParams {
    contentId: string;
    onRequestFailed: (info: ChannelProgramModalErrorCallbackInfo) => void;
}

export function useChannelProgramInfoQuery({
    contentId,
    onRequestFailed,
}: UseChannelProgramInfoQueryParams) {
    const client = useGraphqlClient();

    const [result] = useResources(
        channelProgramInfoResourceQuery(
            {
                client,
                contentId,
                onRequestFailed,
            },
            { lifetime: CHANNEL_PROGRAM_INFO_CACHE_LIFETIME_MS }
        )
    );

    const programs = result.data?.programs;

    return {
        programs,
    };
}
